import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { IDynamicModel } from '../models';
import { findNestedFormGroupsWith } from './dynamic-helpers';
import { dynamicClearTargetVisibility } from './dynamic-show-on-values';

/**
 * Remembers the state each dynamic control was created in, so a form reset can
 * put it back. showOnValues hides and disables its targets as answers change,
 * and a reset pushes null through every controlling question, which would
 * otherwise leave a question that both is shown by another and controls its
 * own follow-ups hidden and disabled (#34303).
 *
 * A fresh open creates every control enabled unless `config.disabled`, and
 * shows or hides it by the configured `options.hidden`.
 */
export class DynamicFreshControlState {
  // true while reset() runs; showOnValues ignores the reset's null values
  isResetting = false;
  // keyed by config so the first (configured) hidden flag survives a FormGroup rebuild
  private hiddenByConfig = new WeakMap<IDynamicModel, boolean | undefined>();
  private configByControl = new WeakMap<AbstractControl, IDynamicModel>();

  record(control: AbstractControl | null, config: IDynamicModel) {
    if (!control || config.isFormBuilder) {
      return;
    }
    if (!this.hiddenByConfig.has(config)) {
      this.hiddenByConfig.set(config, config.options?.hidden);
    }
    this.configByControl.set(control, config);
  }

  /** Puts every recorded control back to its fresh-open state, then resets the values. */
  reset(formGroup: UntypedFormGroup) {
    const nestedFormGroups = findNestedFormGroupsWith(formGroup);
    this.isResetting = true;
    try {
      for (const group of [formGroup, ...nestedFormGroups]) {
        this.restore(group);
      }
      // the guarded showOnValues listeners skip the reset, so drop their old reports here
      dynamicClearTargetVisibility(formGroup);
      formGroup.reset();
      for (const nestedFormGroup of nestedFormGroups) {
        nestedFormGroup.reset();
      }
    } finally {
      this.isResetting = false;
    }
  }

  private restore(group: UntypedFormGroup) {
    for (const control of Object.values(group.controls)) {
      const config = this.configByControl.get(control);
      if (!config) {
        continue;
      }
      if (config.options) {
        config.options.hidden = this.hiddenByConfig.get(config);
        config.options.valueMatchFound = undefined;
      }
      if (config.disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }
}
